import GrowingSpinner from "../templates/skote/components/GrowingSpinner";
import React, {lazy, Suspense, useEffect, useState} from 'react';
import {Link, Route, Routes, useLocation} from 'react-router-dom';
import {Box, Button, Typography} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import '../App.css';
import ErrorBoundary from '../components/ErrorBoundary';
import {pageChunkLoaders, prefetchPage} from './pageChunkLoaders';
import {loadAppShell} from './appShellChunk';
import {setRouteChunkLoading} from '../helpers/routeLoadingBus';
import messages from '../page/messages/general/messages';

/**
 * The Skote chrome is the only end-user layout now (the old IMS template is no
 * longer offered as a choice). It is code-split: its components AND its scoped
 * stylesheet only download once the shell mounts.
 *
 * The loader is shared with the login screen (`loadAppShell`), so the chunk is
 * usually already on its way (or cached) by the time the shell mounts.
 */
const SkoteLayout = lazy(loadAppShell);
/**
 * One lazy component per registered path. Built once at module scope so route
 * components keep a stable identity across re-renders (no remount).
 */
const pageRoutes: Array<[string, React.LazyExoticComponent<React.ComponentType<any>>]> = Object.keys(
    pageChunkLoaders
).map((path) => [path, lazy(pageChunkLoaders[path])]);

/**
 * Rendered by <Suspense> while a page chunk is being fetched. Signals the
 * global route-loading bus so the Layout can show the slim top progress bar.
 */
const RouteFallback = () => {
    // Delay the spinner slightly: most chunks are prefetched (hover/click/login),
    // so they resolve in a few ms. Showing/removing a spinner in that window reads
    // as a flicker, while the space is reserved immediately (no layout shift).
    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        setRouteChunkLoading(true);
        const timer = window.setTimeout(() => setShowSpinner(true), 200);
        return () => {
            window.clearTimeout(timer);
            setRouteChunkLoading(false);
        };
    }, []);

    return (
        <Box sx={{
            width: '100%',
            minHeight: '55vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {showSpinner && <GrowingSpinner size={52} thickness={4} />}
        </Box>
    );
};

/**
 * Warms the chunk of the current route *outside* the layout <Suspense>.
 *
 * Without it the page chunk could only start downloading once the layout chunk
 * had resolved, because the whole shell (and therefore its children) suspends
 * together. Rendering this sibling means the layout and page chunks are fetched
 * in parallel. Failures are swallowed by `prefetchPage`; the real lazy render
 * still reports any error.
 */
const RouteChunkPrefetcher = () => {
    const location = useLocation();
    useEffect(() => {
        prefetchPage(location.pathname);
    }, [location.pathname]);
    return null;
};

/**
 * Fallback for paths that are not registered routes. Server-driven menus and
 * notifications can point at a page this build does not ship; without a
 * catch-all the layout would render with an empty body (looks like a freeze).
 */
const NotFound = () => (
    <Box sx={{p: 4, textAlign: 'center'}}>
        <Typography variant="h5" gutterBottom>
            <FormattedMessage id={messages.page_not_found_title}/>
        </Typography>
        <Typography color="text.secondary" sx={{mb: 3}}>
            <FormattedMessage id={messages.page_not_found_message}/>
        </Typography>
        <Button component={Link} to="/app/dashboard" variant="contained">
            <FormattedMessage id={messages.page_not_found_back}/>
        </Button>
    </Box>
);

function Application(props:any) {
    const {theme, locale,  setLocale} = props;

    const routesContent = (
        <ErrorBoundary>
            <Suspense fallback={<RouteFallback/>}>
                <Routes>
                    {pageRoutes.map(([path, Page]) => (
                        <Route
                            key={path}
                            path={path}
                            element={<Page theme={theme} locale={locale}/>}
                        />
                    ))}
                    <Route path="*" element={<NotFound/>}/>
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );

    return (
        <>
            <RouteChunkPrefetcher/>
            <Suspense fallback={<RouteFallback/>}>
                <SkoteLayout theme={theme} locale={locale} setLocale={setLocale}>
                    {routesContent}
                </SkoteLayout>
            </Suspense>
        </>
    );
}

export default Application;
